import { useMarked } from "../context/marked"
import { useI18n } from "../context/i18n"
import { useChatLinkTitles, type ChatLinkTarget, type ChatLinkTitleResolver } from "../context/chat-link-titles"
import { useArtifactPaths, type ArtifactPathResolver } from "../context/artifact-paths"
import DOMPurify from "dompurify"
import morphdom from "morphdom"
import { checksum } from "@opencode-ai/core/util/encode"
import { fileUrlFromPath, isAbsolutePath } from "@opencode-ai/core/util/path"
import { ComponentProps, createEffect, createMemo, createResource, createSignal, onCleanup, splitProps } from "solid-js"
import { isServer } from "solid-js/web"
import { stream } from "./markdown-stream"
import { oversized } from "./markdown-limits"
import { artifactFileAccent, chooseIconName, colorWithAlpha, getFileIconUrl } from "./file-icon"

type Entry = {
  hash: string
  html: string
}

const max = 200
const cache = new Map<string, Entry>()

if (typeof window !== "undefined" && DOMPurify.isSupported) {
  DOMPurify.addHook("afterSanitizeAttributes", (node: Element) => {
    if (!(node instanceof HTMLAnchorElement)) return
    if (node.target !== "_blank") return

    const rel = node.getAttribute("rel") ?? ""
    const set = new Set(rel.split(/\s+/).filter(Boolean))
    set.add("noopener")
    set.add("noreferrer")
    node.setAttribute("rel", Array.from(set).join(" "))
  })
}

const config = {
  USE_PROFILES: { html: true, mathMl: true },
  SANITIZE_NAMED_PROPS: true,
  FORBID_TAGS: ["style"],
  FORBID_CONTENTS: ["style", "script"],
}

const iconPaths = {
  copy: '<path d="M6.2513 6.24935V2.91602H17.0846V13.7493H13.7513M13.7513 6.24935V17.0827H2.91797V6.24935H13.7513Z" stroke="currentColor" stroke-linecap="round"/>',
  check: '<path d="M5 11.9657L8.37838 14.7529L15 5.83398" stroke="currentColor" stroke-linecap="square"/>',
}

function sanitize(html: string) {
  if (!DOMPurify.isSupported) return ""
  return DOMPurify.sanitize(html, config)
}

function escape(text: string) {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
}

function fallback(markdown: string) {
  return escape(markdown).replace(/\r\n?/g, "\n").replace(/\n/g, "<br>")
}

// Text past the size limit (markdown-limits.ts) is one text node in a <pre>:
// no parse, no sanitize, no decoration. The button asks for formatting anyway.
export function renderPlain(root: HTMLDivElement, text: string, labels: { notice: string; format: string }) {
  const current = root.querySelector(':scope > [data-slot="markdown-plain"]')
  if (current) {
    if (current.textContent !== text) current.textContent = text
    return
  }
  const notice = document.createElement("div")
  notice.setAttribute("data-slot", "markdown-plain-notice")
  const label = document.createElement("span")
  label.textContent = labels.notice
  const button = document.createElement("button")
  button.type = "button"
  button.setAttribute("data-slot", "markdown-plain-format")
  button.textContent = labels.format
  // Not `append`: packages that build this file with Cloudflare's worker types
  // (enterprise) see HTMLRewriter's Element.append(string), which takes no nodes.
  notice.replaceChildren(label, button)
  const pre = document.createElement("pre")
  pre.setAttribute("data-slot", "markdown-plain")
  pre.textContent = text
  root.replaceChildren(notice, pre)
}

type CopyLabels = {
  copy: string
  copied: string
}

const urlPattern = /^https?:\/\/[^\s<>()`"']+$/
const localPathStartPattern = /\/(?:Users|Volumes|Applications|Library|System|private|tmp|var|opt|usr|etc|home)\//
const localPathLabelPattern = /\s+\p{Lu}[\p{Ll}\d_.-]*(?:\s+[\p{Ll}\d_.-]+)*:\s*$/u

function basenameOfPath(path: string) {
  return path.replace(/\\/g, "/").split("/").filter(Boolean).pop() ?? path
}

function codeUrl(text: string) {
  const href = text.trim().replace(/[),.;!?]+$/, "")
  if (!urlPattern.test(href)) return
  try {
    const url = new URL(href)
    return url.toString()
  } catch {
    return
  }
}

function createIcon(path: string, slot: string) {
  const icon = document.createElement("div")
  icon.setAttribute("data-component", "icon")
  icon.setAttribute("data-size", "small")
  icon.setAttribute("data-slot", slot)
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
  svg.setAttribute("data-slot", "icon-svg")
  svg.setAttribute("fill", "none")
  svg.setAttribute("viewBox", "0 0 20 20")
  svg.setAttribute("aria-hidden", "true")
  svg.innerHTML = path
  icon.appendChild(svg)
  return icon
}

function createCopyButton(labels: CopyLabels) {
  const button = document.createElement("button")
  button.type = "button"
  button.setAttribute("data-component", "icon-button")
  button.setAttribute("data-variant", "secondary")
  button.setAttribute("data-size", "small")
  button.setAttribute("data-slot", "markdown-copy-button")
  button.setAttribute("aria-label", labels.copy)
  button.appendChild(createIcon(iconPaths.copy, "copy-icon"))
  button.appendChild(createIcon(iconPaths.check, "check-icon"))
  return button
}

function setCopyState(button: HTMLButtonElement, labels: CopyLabels, copied: boolean) {
  if (copied) {
    button.setAttribute("data-copied", "true")
    button.setAttribute("aria-label", labels.copied)
    return
  }
  button.removeAttribute("data-copied")
  button.setAttribute("aria-label", labels.copy)
}

function ensureCodeWrapper(block: HTMLPreElement, labels: CopyLabels) {
  const parent = block.parentElement
  if (!parent) return
  const wrapped = parent.getAttribute("data-component") === "markdown-code"
  if (!wrapped) {
    const wrapper = document.createElement("div")
    wrapper.setAttribute("data-component", "markdown-code")
    parent.replaceChild(wrapper, block)
    wrapper.appendChild(block)
    wrapper.appendChild(createCopyButton(labels))
    return
  }

  const buttons = Array.from(parent.querySelectorAll('[data-slot="markdown-copy-button"]')).filter(
    (el): el is HTMLButtonElement => el instanceof HTMLButtonElement,
  )

  if (buttons.length === 0) {
    parent.appendChild(createCopyButton(labels))
    return
  }

  for (const button of buttons.slice(1)) {
    button.remove()
  }
}

function wrapMarkdownTables(root: HTMLDivElement) {
  const tables = Array.from(root.querySelectorAll("table"))
  for (const table of tables) {
    if (table.closest('[data-component="tma-table"]')) continue
    const parent = table.parentElement
    if (!parent) continue

    const wrap = document.createElement("div")
    wrap.setAttribute("data-component", "tma-table")
    const scroll = document.createElement("div")
    scroll.setAttribute("data-slot", "tma-table-scroll")

    parent.replaceChild(wrap, table)
    wrap.appendChild(scroll)
    scroll.appendChild(table)
  }
}

function markCodeLinks(root: HTMLDivElement) {
  const codeNodes = Array.from(root.querySelectorAll(":not(pre) > code"))
  for (const code of codeNodes) {
    const href = codeUrl(code.textContent ?? "")
    const parentLink =
      code.parentElement instanceof HTMLAnchorElement && code.parentElement.classList.contains("external-link")
        ? code.parentElement
        : null

    if (!href) {
      if (parentLink) parentLink.replaceWith(code)
      continue
    }

    if (parentLink) {
      parentLink.href = href
      continue
    }

    const link = document.createElement("a")
    link.href = href
    link.className = "external-link"
    link.target = "_blank"
    link.rel = "noopener noreferrer"
    code.parentNode?.replaceChild(link, code)
    link.appendChild(code)
  }
}

export function localPathRanges(text: string) {
  const ranges: Array<{ start: number; end: number; path: string }> = []
  let offset = 0

  while (offset < text.length) {
    const startMatch = localPathStartPattern.exec(text.slice(offset))
    if (!startMatch) return ranges

    const start = offset + startMatch.index
    const nextLine = text.indexOf("\n", start)
    const nextPathMatch = localPathStartPattern.exec(text.slice(start + 1))
    const rawEnd = Math.min(
      nextLine === -1 ? text.length : nextLine,
      nextPathMatch ? start + 1 + nextPathMatch.index : text.length,
    )
    const raw = text.slice(start, rawEnd)
    const trimmedStart = raw.search(/\S/)
    const value = raw
      .slice(trimmedStart === -1 ? 0 : trimmedStart)
      .replace(localPathLabelPattern, "")
      .trim()
      // Sentence punctuation glued to the path — «сохранена как /Users/…/deck.pptx.»
      // — must not become part of the path/chip label (same set codeUrl strips).
      .replace(/[),.;!?]+$/, "")
    if (!value) {
      offset = rawEnd + 1
      continue
    }

    ranges.push({ start, end: start + value.length, path: value })
    offset = rawEnd
  }

  return ranges
}

function styleArtifactLink(link: HTMLAnchorElement, filename: string) {
  link.classList.add("gigatool-artifact-link", "artifact-link-with-icon")
  const color = artifactFileAccent(filename)
  link.style.setProperty("--artifact-link-color", color)
  link.style.setProperty("--artifact-link-background", colorWithAlpha(color, 0.1))
}

function appendArtifactLinkLabel(
  link: HTMLAnchorElement,
  filename: string,
  code?: HTMLElement,
  kind: "file" | "directory" = "file",
) {
  styleArtifactLink(link, filename)
  link.appendChild(createArtifactFileIcon(filename, kind))
  if (code) {
    code.textContent = filename
    link.appendChild(code)
    return
  }
  const label = document.createElement("code")
  label.textContent = filename
  link.appendChild(label)
}

function linkLocalPaths(root: HTMLDivElement) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement
      if (!parent) return NodeFilter.FILTER_REJECT
      if (parent.closest("a, pre, code")) return NodeFilter.FILTER_REJECT
      if (!localPathStartPattern.test(node.textContent ?? "")) return NodeFilter.FILTER_REJECT
      return NodeFilter.FILTER_ACCEPT
    },
  })
  const nodes: Text[] = []

  while (true) {
    const node = walker.nextNode()
    if (!node) break
    nodes.push(node as Text)
  }

  for (const node of nodes) {
    const text = node.textContent ?? ""
    const ranges = localPathRanges(text)
    if (ranges.length === 0) continue

    const fragment = document.createDocumentFragment()
    let cursor = 0
    for (const range of ranges) {
      if (range.start > cursor) fragment.append(text.slice(cursor, range.start))

      const link = document.createElement("a")
      link.href = `file://${encodeURI(range.path)}`
      link.className = "external-link"
      link.target = "_blank"
      link.rel = "noopener noreferrer"
      link.dataset.localPath = range.path
      link.title = range.path
      appendArtifactLinkLabel(link, basenameOfPath(range.path))
      fragment.append(link)
      cursor = range.end
    }
    if (cursor < text.length) fragment.append(text.slice(cursor))
    node.replaceWith(fragment)
  }
}

// Returns the absolute local path that a bare inline-code string represents
// (the whole code IS a path, e.g. `/Users/me/deck.pptx`), or undefined when
// the code merely mentions one mid-string (e.g. `python /Users/me/run.py`,
// which we don't want to make wholly clickable) or isn't a path at all.
export function codeLocalPath(text: string): string | undefined {
  const trimmed = text.trim()
  if (!localPathStartPattern.test(trimmed)) return
  const range = localPathRanges(trimmed)[0]
  if (!range || range.start !== 0) return
  return range.path
}

// GigaTool: models often wrap an artifact path in inline code — e.g.
// "Файл: `/Users/.../vidy-irisov.pptx`" — which markdown renders as a
// <code> element. linkLocalPaths walks text nodes and deliberately skips
// anything inside <code>/<pre>, so those paths never become clickable. Catch
// that case here: when an inline <code> element's content *is* an absolute
// local path, wrap the whole element in a file:// link (same scheme as
// linkLocalPaths) so the renderer's global click delegate reveals it in
// Finder, exactly like a bare path in prose.
function linkLocalPathCode(root: HTMLDivElement) {
  const codes = Array.from(root.querySelectorAll("code"))
  for (const code of codes) {
    // Skip block-level code (inside <pre>) and already-wrapped code.
    if (code.parentElement instanceof HTMLPreElement) continue
    if (code.parentElement instanceof HTMLAnchorElement) continue
    const path = codeLocalPath(code.textContent ?? "")
    if (!path) continue
    const a = document.createElement("a")
    a.href = `file://${encodeURI(path)}`
    a.className = "external-link"
    a.target = "_blank"
    a.rel = "noopener noreferrer"
    a.dataset.localPath = path
    a.title = path
    code.parentNode?.insertBefore(a, code)
    appendArtifactLinkLabel(a, basenameOfPath(path), code)
  }
}

export function decorate(
  root: HTMLDivElement,
  labels: CopyLabels,
  opts: {
    linkArtifacts: boolean
    artifactFilenames?: Set<string>
    resolveArtifactPath?: ArtifactPathResolver
    chatLinkLabel: string
    resolveChatTitle?: ChatLinkTitleResolver
  },
) {
  const blocks = Array.from(root.querySelectorAll("pre"))
  for (const block of blocks) {
    ensureCodeWrapper(block, labels)
  }
  wrapMarkdownTables(root)
  markCodeLinks(root)
  // Chat-session links MUST be linkified before linkLocalPaths: a chat link's
  // directory param is an absolute path (/Users/…, /home/…), so linkLocalPaths
  // would otherwise greedily wrap the URL's tail into a file:// anchor and
  // split the text node before linkChatSessions ever sees the intact URL.
  // Once wrapped in an <a>, linkLocalPaths skips it (it rejects nodes under a/pre/code).
  linkChatSessions(root, opts.chatLinkLabel, opts.resolveChatTitle)
  linkLocalPaths(root)
  linkLocalPathCode(root)
  // Plan-mode messages don't actually create files, so we skip turning
  // inline-code filenames into clickable artifact links — the resolution
  // would fail and the click would feel broken.
  if (opts.linkArtifacts) linkArtifactFilenames(root, opts.artifactFilenames, opts.resolveArtifactPath)
}

/**
 * decorate(), contained. The post-pass is cosmetic — chips, copy buttons,
 * table wrappers — and it runs on model text in a render effect, so a throw
 * there reached the app's only ErrorBoundary and put the whole window on the
 * error page, again on every open of that chat (#243: a `…/proto/` folder chip
 * whose icon lookup found Object.prototype). A message without its chips is
 * still the message: on failure the root is reset to the undecorated HTML.
 * Returns whether decoration succeeded.
 */
export function decorateOrPlain(
  root: HTMLDivElement,
  html: string,
  labels: CopyLabels,
  opts: Parameters<typeof decorate>[2],
): boolean {
  try {
    decorate(root, labels, opts)
    return true
  } catch (error) {
    console.error("[markdown] decoration failed — showing the message undecorated", error)
    root.innerHTML = html
    return false
  }
}

// GigaTool: chat deep links (`gigatool://open-session?directory=…&session=…`,
// legacy `opencode://` scheme) pasted into a message would otherwise render as
// an opaque wall of URL text. Sanitization strips anchors with custom schemes,
// so this post-pass rebuilds them: the raw URL becomes a compact labelled link
// ("Chat · <folder>") that the renderer's global click delegate routes back
// into the app as a deep link.
export const chatSessionLinkPattern = /(?:gigatool|opencode):\/\/open-session\?[^\s<>()"'`]+/g

export function chatSessionLinkTarget(href: string): ChatLinkTarget {
  try {
    const params = new URL(href).searchParams
    return {
      directory: params.get("directory") ?? undefined,
      session: params.get("session") ?? undefined,
    }
  } catch {
    return {}
  }
}

export function chatSessionLinkText(href: string, label: string, resolveTitle?: ChatLinkTitleResolver) {
  const target = chatSessionLinkTarget(href)
  const title = resolveTitle?.(target)?.trim()
  if (title) return `${label} · ${title}`
  const basename = target.directory?.split(/[\\/]/).filter(Boolean).at(-1)
  if (!basename) return label
  // Auto-created home-chat folders are named Chat-N, so the default label
  // would read as a stutter («Чат · Chat-263»). Collapse it to the localized
  // prefix + number instead.
  const generic = basename.match(/^chat[-_ ]?(\d+)$/i)
  if (generic) return `${label}-${generic[1]}`
  return `${label} · ${basename}`
}

function linkChatSessions(root: HTMLDivElement, label: string, resolveTitle?: ChatLinkTitleResolver) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement
      if (!parent) return NodeFilter.FILTER_REJECT
      if (parent.closest("a, pre, code")) return NodeFilter.FILTER_REJECT
      if (!/(?:gigatool|opencode):\/\/open-session\?/.test(node.textContent ?? "")) return NodeFilter.FILTER_REJECT
      return NodeFilter.FILTER_ACCEPT
    },
  })
  const nodes: Text[] = []

  while (true) {
    const node = walker.nextNode()
    if (!node) break
    nodes.push(node as Text)
  }

  for (const node of nodes) {
    const text = node.textContent ?? ""
    const matches = Array.from(text.matchAll(chatSessionLinkPattern))
    if (matches.length === 0) continue

    const fragment = document.createDocumentFragment()
    let cursor = 0
    for (const match of matches) {
      if (match.index > cursor) fragment.append(text.slice(cursor, match.index))

      const href = match[0].replace(/[),.;!?]+$/, "")
      const link = document.createElement("a")
      link.href = href
      link.className = "external-link chat-session-link"
      link.target = "_blank"
      link.rel = "noopener noreferrer"
      link.textContent = chatSessionLinkText(href, label, resolveTitle)
      fragment.append(link)
      cursor = match.index + href.length
    }
    if (cursor < text.length) fragment.append(text.slice(cursor))
    node.replaceWith(fragment)
  }
}

// GigaTool: turn inline `<code>` elements whose text is a bare artifact
// filename (e.g. `Шаблон_БЖУ.xlsx`) into clickable links. We don't know the
// full path here — that's resolved later by a session-level listener that
// scans recent tool-write parts. The href uses a synthetic
// gigatool-artifact:// scheme so the renderer's global click delegate can
// recognise these and dispatch a custom event for resolution.
//
// Extensions allowlist: what file-preview/util.ts opens, plus the plain
// documents a model hands over beside them — a LaTeX source next to its PDF,
// a .txt, an OpenDocument file (30.09: `Ключ.pdf` was a chip, `Ключ.tex`
// beside it plain grey text).
const ARTIFACT_EXTENSIONS =
  "json|py|pdf|csv|tsv|xlsx?|pptx?|docx?|md|markdown|html?|png|jpe?g|gif|webp|svg|tex|txt|rtf|od[tsp]"
// Document names may contain spaces — «План и конспект.pdf», «Урок 5 — Дроби.pdf»,
// «Снимок экрана 2026-09-30.png» — and those were never linked: the field
// listing of a lesson's files came out five grey names to two blue ones. Code
// and data extensions stay single-word, so `python3 main.py` is not a file.
const DOCUMENT_EXTENSIONS =
  "pdf|csv|tsv|xlsx?|pptx?|docx?|md|markdown|html?|png|jpe?g|gif|webp|svg|tex|txt|rtf|od[tsp]"
// Letters (Latin with accents, Cyrillic), digits and what names carry: dots,
// hyphens and dashes, brackets, «guillemets», №.
const NAME_CHARS = "[\\wÀ-ÿЀ-ӿ.\\-–—()+№«»]"
// Words of a name are split by spaces — including the narrow no-break space
// macOS puts before "AM" in an English screenshot's name.
const NAME_GAP = "[ \\u00a0\\u202f]+"
const ARTIFACT_FILENAME_PATTERN = new RegExp(`^${NAME_CHARS}+\\.(${ARTIFACT_EXTENSIONS})$`, "i")
const SPACED_DOCUMENT_PATTERN = new RegExp(
  `^${NAME_CHARS}+(?:${NAME_GAP}${NAME_CHARS}+)+\\.(${DOCUMENT_EXTENSIONS})$`,
  "i",
)
// One word with an extension: worth asking the host whether the session wrote
// it (see linkArtifactFilenames).
const FILENAME_SHAPE = new RegExp(`^${NAME_CHARS}+\\.[a-z0-9]{1,8}$`, "i")
// A spaced name is a command when it reads like one: `cat report.md`,
// `pdflatex Ключ.tex`, `Get-Content report.txt`, `pandoc notes -o out.pdf`.
// Case-sensitive on purpose: commands are typed lowercase, while «Copy of
// report.docx» (Google Drive's name for a duplicate) or «Open Day.pdf» are
// files.
const COMMAND_WORDS = new Set(
  (
    "cat less more head tail open start code vim vi nano rm del cp mv copy move ls dir type touch mkdir cd " +
    "python python3 node bun npm npx pip multitool-node pdflatex xelatex lualatex latex latexmk tectonic " +
    "pandoc soffice libreoffice git curl wget echo printf grep rg find chmod zip unzip tar convert magick " +
    "ffmpeg sudo notepad explorer xdg-open ii"
  ).split(" "),
)
// PowerShell's Verb-Noun: Get-Content, Remove-Item, Start-Process. Its own
// verbs only — «Year-End Report.pdf» and «Follow-Up notes.docx» are names.
const CMDLET =
  /^(?:Get|Set|New|Remove|Start|Stop|Invoke|Copy|Move|Rename|Out|Select|Import|Export|Test|Add|Clear|Write|Read|Expand|Compress|ConvertTo|ConvertFrom)-[A-Z]/
// A flag, not a dash inside a name: `-o`, `--output`, but not «Урок 5 - Дроби».
const FLAG = /^--?[A-Za-z]/
const EXTENSION_END = new RegExp(`\\.(${ARTIFACT_EXTENSIONS})$`, "i")

// A path relative to the chat's folder, as models list what they made:
// `02 Уроки/Русский язык/9 класс/` (a folder — the trailing slash says so),
// `docs/decide.md`. Words of a segment may be spaced like a name's.
const RELATIVE_SEGMENT = `${NAME_CHARS}+(?:${NAME_GAP}${NAME_CHARS}+)*`
const RELATIVE_PATH = new RegExp(`^(?:\\./)?(?:${RELATIVE_SEGMENT}/)+(?:${RELATIVE_SEGMENT})?$`)

/**
 * An inline-code token that is a relative path to a folder (ends in `/`) or to
 * a file whose name we would link on its own. Undefined for anything else —
 * `feat/branch`, `и/или`, `owner/repo` carry neither mark.
 */
export function relativeArtifactPath(text: string): { directory: boolean } | undefined {
  if (text.length > 300 || !RELATIVE_PATH.test(text)) return undefined
  const segments = text.replace(/^\.\//, "").split("/")
  const last = segments[segments.length - 1]
  // No step up or sideways: `..`, and any segment of nothing but dots and
  // spaces (`...`, `.. ..`), which some systems trim to a parent reference.
  if (segments.some((segment) => /^[.\s\u00a0\u202f]+$/.test(segment) || FLAG.test(segment))) return undefined
  if (last === "") return segments.length > 1 ? { directory: true } : undefined // `./` names nothing
  return artifactFilename(last) ? { directory: false } : undefined
}

/** Whether an inline-code token names a file by itself (see linkArtifactFilenames). */
export function artifactFilename(text: string) {
  if (ARTIFACT_FILENAME_PATTERN.test(text)) return true
  if (text.length > 160 || !SPACED_DOCUMENT_PATTERN.test(text)) return false
  const words = text.split(/[ \u00a0\u202f]+/)
  if (COMMAND_WORDS.has(words[0]) || CMDLET.test(words[0])) return false
  if (words.some((word) => FLAG.test(word))) return false
  // Two names in one span (`a.txt b.txt`, `README.md и CHANGELOG.md`) are not one file.
  return words.filter((word) => EXTENSION_END.test(word)).length === 1
}

let artifactIconId = 0

function createArtifactFileIcon(filename: string, kind: "file" | "directory" = "file") {
  const id = `artifact-file-icon-${artifactIconId++}`
  const source = getFileIconUrl(chooseIconName(filename.replace(/\/+$/, ""), kind, false))
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
  svg.setAttribute("data-component", "file-icon")
  svg.setAttribute("class", "artifact-link-icon")
  svg.setAttribute("aria-hidden", "true")
  const defs = document.createElementNS("http://www.w3.org/2000/svg", "defs")
  const mask = document.createElementNS("http://www.w3.org/2000/svg", "mask")
  const image = document.createElementNS("http://www.w3.org/2000/svg", "image")
  const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect")
  mask.setAttribute("id", id)
  mask.setAttribute("mask-type", "alpha")
  image.setAttribute("href", source)
  image.setAttribute("width", "100%")
  image.setAttribute("height", "100%")
  image.setAttribute("preserveAspectRatio", "xMidYMid meet")
  rect.setAttribute("width", "100%")
  rect.setAttribute("height", "100%")
  rect.setAttribute("fill", "currentColor")
  rect.setAttribute("mask", `url(#${id})`)
  mask.appendChild(image)
  defs.appendChild(mask)
  svg.appendChild(defs)
  svg.appendChild(rect)
  return svg
}

function linkArtifactFilenames(root: HTMLDivElement, knownFiles?: Set<string>, resolvePath?: ArtifactPathResolver) {
  const codes = Array.from(root.querySelectorAll("code"))
  for (const code of codes) {
    // Skip block-level code (inside <pre>) and already-wrapped code.
    if (code.parentElement instanceof HTMLPreElement) continue
    if (code.parentElement instanceof HTMLAnchorElement) continue
    const text = (code.textContent ?? "").trim()
    // A relative path (`02 Уроки/…/9А/`): opened against the chat's folder at
    // click time (session.tsx), so it needs no resolver here. Field case
    // (30.09): the lesson's folder was the one grey chip left in a listing.
    const relative = relativeArtifactPath(text)
    if (relative) {
      const a = document.createElement("a")
      a.href = `gigatool-artifact://${encodeURIComponent(text)}`
      a.className = "external-link gigatool-artifact-link artifact-link-with-icon"
      a.target = "_blank"
      a.rel = "noopener noreferrer"
      a.dataset.artifactFilename = text
      code.parentNode?.insertBefore(a, code)
      appendArtifactLinkLabel(a, text, code, relative.directory ? "directory" : "file")
      continue
    }
    // Two independent signals that an inline-code token is an openable file:
    //  1. its extension is one we recognise as a document/data/image, OR
    //  2. it's the exact basename of a file the model wrote/edited this turn
    //     (knownFiles). This catches source files like `deck.js` whose
    //     extension is deliberately kept OUT of the static allowlist — adding
    //     `js`/`ts`/… there would also linkify prose like `Node.js`, which
    //     resolves to nothing. Keying off files we know were produced makes
    //     any extension safe and guarantees the click resolves.
    //  3. the host resolves it to a file the SESSION wrote — `deck.js` from an
    //     earlier turn, when a later one («покажи файлы») lists what was made.
    //     Asked only for a one-word name with an extension: the resolver walks
    //     the session's parts, and this runs on every streamed render.
    const named = artifactFilename(text) || !!knownFiles?.has(text)
    if (!named && !FILENAME_SHAPE.test(text)) continue
    // When the host can resolve the basename to an absolute path (a file the
    // model wrote), emit a real file:// chip: it gets the full OS context
    // menu (Open / Show in Folder / Copy path) and reveals on click, identical
    // to an absolute-path chip in prose. Otherwise fall back to the synthetic
    // gigatool-artifact:// scheme, which the global click delegate resolves
    // lazily. encodeURI (not encodeURIComponent) matches how the delegate and
    // the main-process menu decode file:// hrefs (decodeURI of the tail).
    const resolved = resolvePath?.(text)
    const absolute = !!resolved && isAbsolutePath(resolved)
    if (!named && !absolute) continue
    const a = document.createElement("a")
    if (resolved && absolute) {
      // fileUrlFromPath (not a bare encodeURI): a Windows path is backslash-
      // separated, so encodeURI alone leaves zero `/` after file:// — an
      // unusable URL. This emits file:///C:/… and round-trips through the
      // desktop click delegate's decodeURI.
      a.href = fileUrlFromPath(resolved)
      a.dataset.localPath = resolved
    } else {
      a.href = `gigatool-artifact://${encodeURIComponent(text)}`
    }
    // .external-link routes the click through the renderer's global delegate
    // (packages/desktop-electron/src/renderer/index.tsx::handleClick), which
    // looks at the URL scheme. .gigatool-artifact-link is a marker we can
    // style or query independently if needed.
    a.className = "external-link gigatool-artifact-link artifact-link-with-icon"
    a.target = "_blank"
    a.rel = "noopener noreferrer"
    a.dataset.artifactFilename = text
    code.parentNode?.insertBefore(a, code)
    appendArtifactLinkLabel(a, text, code)
  }
}

function setupCodeCopy(root: HTMLDivElement, getLabels: () => CopyLabels) {
  const timeouts = new Map<HTMLButtonElement, ReturnType<typeof setTimeout>>()

  const updateLabel = (button: HTMLButtonElement) => {
    const labels = getLabels()
    const copied = button.getAttribute("data-copied") === "true"
    setCopyState(button, labels, copied)
  }

  const handleClick = async (event: MouseEvent) => {
    const target = event.target
    if (!(target instanceof Element)) return

    const button = target.closest('[data-slot="markdown-copy-button"]')
    if (!(button instanceof HTMLButtonElement)) return
    const code = button.closest('[data-component="markdown-code"]')?.querySelector("code")
    const content = code?.textContent ?? ""
    if (!content) return
    const clipboard = navigator?.clipboard
    if (!clipboard) return
    await clipboard.writeText(content)
    const labels = getLabels()
    setCopyState(button, labels, true)
    const existing = timeouts.get(button)
    if (existing) clearTimeout(existing)
    const timeout = setTimeout(() => setCopyState(button, labels, false), 2000)
    timeouts.set(button, timeout)
  }

  const buttons = Array.from(root.querySelectorAll('[data-slot="markdown-copy-button"]'))
  for (const button of buttons) {
    if (button instanceof HTMLButtonElement) updateLabel(button)
  }

  root.addEventListener("click", handleClick)

  return () => {
    root.removeEventListener("click", handleClick)
    for (const timeout of timeouts.values()) {
      clearTimeout(timeout)
    }
  }
}

function touch(key: string, value: Entry) {
  cache.delete(key)
  cache.set(key, value)

  if (cache.size <= max) return

  const first = cache.keys().next().value
  if (!first) return
  cache.delete(first)
}

export function Markdown(
  props: ComponentProps<"div"> & {
    text: string
    variant?: "document"
    cacheKey?: string
    streaming?: boolean
    class?: string
    classList?: Record<string, boolean>
    // GigaTool: turn off the artifact-filename anchoring for messages where
    // no files are actually produced (plan mode). Defaults to true so the
    // existing call sites (assistant messages in build/ask/etc.) keep
    // working unchanged.
    linkArtifacts?: boolean
    // GigaTool: basenames of files the model wrote/edited this turn. An
    // inline-code token matching one of these is linkified regardless of its
    // extension (see linkArtifactFilenames), so source files like `deck.js`
    // become clickable while prose like `Node.js` does not.
    artifactFilenames?: string[]
  },
) {
  const [local, others] = splitProps(props, [
    "text",
    "variant",
    "cacheKey",
    "streaming",
    "class",
    "classList",
    "linkArtifacts",
    "artifactFilenames",
  ])
  const marked = useMarked()
  const i18n = useI18n()
  const resolveChatTitle = useChatLinkTitles()
  const resolveArtifactPath = useArtifactPaths()
  const [root, setRoot] = createSignal<HTMLDivElement>()
  const [formatted, setFormatted] = createSignal(false)
  const plain = createMemo(() => !formatted() && oversized(local.text))

  // A streaming render may start only after the previous one has been idle
  // for three times as long as it took, so rendering holds the main thread at
  // most a quarter of the time however expensive one render is. An ordinary
  // message renders in a few ms and keeps the 24 ms reveal pace untouched; a
  // huge or pathological one reveals in coarser steps instead of re-parsing
  // on every tick and never yielding — the #218 freeze. A finished message is
  // never held back.
  const [shown, setShown] = createSignal(local.text)
  let cost = 0
  let parsed = 0
  let settled = 0
  let wait: ReturnType<typeof setTimeout> | undefined
  const release = () => {
    if (wait) clearTimeout(wait)
    wait = undefined
  }
  createEffect(() => {
    const text = local.text
    const delay = settled + cost * 3 - performance.now()
    if (!local.streaming || delay <= 0) {
      release()
      setShown(text)
      return
    }
    if (wait) return
    wait = setTimeout(() => {
      wait = undefined
      setShown(local.text)
    }, delay)
  })
  onCleanup(release)

  // HTML of the blocks a live render has already parsed, by position: while
  // streaming, the blocks before the tail almost never change
  // (markdown-stream.ts), so a tick re-parses only the tail. Kept per
  // instance — a long answer has more blocks than the shared cache holds, and
  // would evict every other message from it.
  const live = new Map<number, { src: string; html: string }>()

  const [html] = createResource(
    () =>
      !plain() && {
        text: shown(),
        key: local.cacheKey,
        streaming: local.streaming ?? false,
      },
    async (src) => {
      if (isServer) return fallback(src.text)
      if (!src.text) return ""

      const started = performance.now()
      const base = src.key ?? checksum(src.text)
      const blocks = stream(src.text, src.streaming)
      for (const index of live.keys()) if (!src.streaming || index >= blocks.length) live.delete(index)
      return Promise.all(
        blocks.map(async (block, index) => {
          if (block.mode === "live") {
            const hit = live.get(index)
            if (hit && hit.src === block.src) return hit.html
            const next = sanitize(await Promise.resolve(marked.parse(block.src)))
            live.set(index, { src: block.src, html: next })
            return next
          }

          const hash = checksum(block.raw)
          const key = base ? `${base}:${index}:${block.mode}` : hash

          if (key && hash) {
            const cached = cache.get(key)
            if (cached && cached.hash === hash) {
              touch(key, cached)
              return cached.html
            }
          }

          const next = await Promise.resolve(marked.parse(block.src))
          const safe = sanitize(next)
          if (key && hash) touch(key, { hash, html: safe })
          return safe
        }),
      )
        .then((list) => {
          parsed = performance.now() - started
          return list.join("")
        })
        .catch(() => fallback(src.text))
    },
    { initialValue: fallback(local.text) },
  )

  let copyCleanup: (() => void) | undefined

  createEffect(() => {
    const container = root()
    if (!container) return
    const format = (event: MouseEvent) => {
      if (event.target instanceof Element && event.target.closest('[data-slot="markdown-plain-format"]'))
        setFormatted(true)
    }
    container.addEventListener("click", format)
    onCleanup(() => container.removeEventListener("click", format))
  })

  createEffect(() => {
    const container = root()
    if (!container) return
    if (isServer) return

    if (plain()) {
      const started = performance.now()
      renderPlain(container, shown(), {
        notice: i18n.t("ui.markdown.plain.notice"),
        format: i18n.t("ui.markdown.plain.format"),
      })
      cost = performance.now() - started
      settled = performance.now()
      return
    }

    // Not keyed on the text itself: a new text means a new resource value
    // anyway, and reading it here re-ran the whole DOM pass once more per
    // render with the previous HTML.
    const content = html.latest ?? html() ?? ""
    if (!content) {
      container.innerHTML = ""
      return
    }

    const started = performance.now()

    const labels = {
      copy: i18n.t("ui.message.copy"),
      copied: i18n.t("ui.message.copied"),
    }
    const temp = document.createElement("div")
    temp.innerHTML = content
    const known = local.artifactFilenames
    decorateOrPlain(temp, content, labels, {
      linkArtifacts: local.linkArtifacts ?? true,
      artifactFilenames: known && known.length ? new Set(known) : undefined,
      resolveArtifactPath,
      chatLinkLabel: i18n.t("ui.message.chatLink"),
      resolveChatTitle,
    })

    morphdom(container, temp, {
      childrenOnly: true,
      onBeforeElUpdated: (fromEl, toEl) => {
        if (
          fromEl instanceof HTMLButtonElement &&
          toEl instanceof HTMLButtonElement &&
          fromEl.getAttribute("data-slot") === "markdown-copy-button" &&
          toEl.getAttribute("data-slot") === "markdown-copy-button" &&
          fromEl.getAttribute("data-copied") === "true"
        ) {
          setCopyState(toEl, labels, true)
        }
        if (fromEl.isEqualNode(toEl)) return false
        return true
      },
    })
    cost = parsed + performance.now() - started
    settled = performance.now()

    if (!copyCleanup)
      copyCleanup = setupCodeCopy(container, () => ({
        copy: i18n.t("ui.message.copy"),
        copied: i18n.t("ui.message.copied"),
      }))
  })

  onCleanup(() => {
    if (copyCleanup) copyCleanup()
  })

  return (
    <div
      data-component="markdown"
      data-variant={local.variant}
      classList={{
        ...local.classList,
        [local.class ?? ""]: !!local.class,
      }}
      ref={setRoot}
      {...others}
    />
  )
}
